import { ReceiptText } from 'lucide-react';
import StatusBadge from '../ui/StatusBadge';
import { formatPrice, formatShortDate } from '../../lib/format';
import { MAIN_SITE_ORIGIN } from '../../lib/chatMappers';

/**
 * Which side of ONE order the signed-in user is on.
 *
 * The endpoint matches both directions at once - orders I bought from this
 * counterparty AND orders I sold to them - so the order's own `buyerId` /
 * `sellerId` are the only per-row truth. They are compared against the signed-in
 * user; an older API that does not send them yet falls back to the account's
 * role, which the chat already uses to pick the buyer/seller side of the inbox.
 *
 * `null` means "cannot tell" - and an unknown side must not produce a link,
 * because the buyer's order page and the seller's orders page are different
 * destinations and guessing would send half the rows somewhere wrong.
 */
export function orderSide(order, viewerId, viewerRole) {
  if (order?.buyerId || order?.sellerId) {
    if (viewerId && order.buyerId === viewerId) return 'BUYER';
    if (viewerId && order.sellerId === viewerId) return 'SELLER';
    return null;
  }

  return viewerRole === 'BUYER' || viewerRole === 'SELLER' ? viewerRole : null;
}

/**
 * Where a row points, on the marketplace origin - the chat is its own subdomain,
 * so a relative href would 404 here.
 *
 *   buyer  -> /order-details.html?id=<order uuid>  (the form the marketplace
 *             itself builds in buyer-dashboard/orders.html, cart.html, ...)
 *   seller -> /seller-dashboard/orders?orderId=<order uuid>, the seller orders
 *             page with that order's modal already open (seller-dashboard/
 *             orders.html reads `orderId`)
 *
 * Exported for the render check; returns null when there is nothing safe to
 * link to (no id, or no way to tell which side the viewer is on).
 */
export function orderHref(order, viewerId, viewerRole) {
  if (!order?.id) return null;

  const id = encodeURIComponent(order.id);
  const side = orderSide(order, viewerId, viewerRole);

  if (side === 'SELLER') return `${MAIN_SITE_ORIGIN}/seller-dashboard/orders?orderId=${id}`;
  if (side === 'BUYER') return `${MAIN_SITE_ORIGIN}/order-details.html?id=${id}`;
  return null;
}

/**
 * "My Orders" tab - real orders between the two accounts, from
 * GET /api/orders/chat-context?counterpartyId=..., which covers both directions
 * (you as buyer and you as seller).
 *
 * Laid out as rows rather than a five-column <table>: the details panel is
 * 300px wide, and a real table would wrap or force a horizontal scroll.
 *
 * Every row that has somewhere to go is a real anchor to the marketplace, opened
 * in a new tab so the conversation stays put: middle-click and ctrl/middle-click
 * work, and the row is reachable by keyboard. A row without an id (or without a
 * resolvable side) stays plain text rather than becoming a dead link.
 */
export default function OrdersList({ orders, status, participantName, viewerId, viewerRole }) {
  if (status === 'loading') {
    return (
      <section className="space-y-2 rounded-2xl border border-line bg-surface p-4 shadow-soft">
        <span className="sr-only" role="status">
          Loading orders…
        </span>
        {[0, 1, 2].map((row) => (
          <span key={row} className="block space-y-2">
            <span className="block h-3 w-3/4 animate-pulse rounded bg-surface-3" />
            <span className="block h-2.5 w-1/2 animate-pulse rounded bg-surface-3" />
          </span>
        ))}
      </section>
    );
  }

  if (status === 'error') {
    return (
      <section className="rounded-2xl border border-line bg-surface px-4 py-6 text-center shadow-soft">
        <p className="text-[12.5px] font-medium text-ink">Couldn’t load orders</p>
        <p className="mt-1 text-[11.5px] text-ink-3">Try reopening this conversation.</p>
      </section>
    );
  }

  if (orders.length === 0) {
    return (
      <section className="rounded-2xl border border-line bg-surface px-4 py-6 text-center shadow-soft">
        <span className="mx-auto grid h-10 w-10 place-items-center rounded-xl bg-surface-3 text-ink-3">
          <ReceiptText size={18} />
        </span>
        <p className="mt-2.5 text-[12.5px] font-medium text-ink">No orders yet</p>
        <p className="mt-1 text-[11.5px] text-ink-3">
          Orders between you and {participantName} will appear here.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label="Recent orders"
      className="overflow-hidden rounded-2xl border border-line bg-surface shadow-soft"
    >
      <header className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink">
          <ReceiptText size={14} className="text-accent" />
          Orders
        </span>
        <span className="text-[10.5px] text-ink-3 tabular-nums">
          {`${orders.length} with ${participantName}`}
        </span>
      </header>

      <ul className="divide-y divide-line">
        {orders.map((order) => {
          const href = orderHref(order, viewerId, viewerRole);

          const body = (
            <>
              <span className="flex items-start justify-between gap-3">
                <span className="line-clamp-2 text-[12.5px] leading-snug font-medium text-ink">
                  {order.item}
                </span>
                <span className="shrink-0 text-[12.5px] font-semibold text-ink tabular-nums">
                  {formatPrice(order.price)}
                </span>
              </span>

              <span className="mt-1.5 flex items-center gap-2">
                <span className="text-[10.5px] text-ink-3 tabular-nums">{order.orderNumber}</span>
                <span aria-hidden="true" className="text-ink-3">
                  ·
                </span>
                <span className="text-[10.5px] text-ink-3">{formatShortDate(order.at)}</span>
                <StatusBadge status={order.status} className="ml-auto" />
              </span>
            </>
          );

          // `[outline-offset:-2px]` keeps the app-wide focus ring inside the
          // rounded, `overflow-hidden` card instead of being clipped by it.
          const rowClass = 'block px-4 py-2.5';

          return (
            <li key={order.id}>
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${rowClass} transition-colors hover:bg-surface-3 focus-visible:bg-surface-3 focus-visible:[outline-offset:-2px]`}
                >
                  {body}
                  <span className="sr-only">
                    {`Open order ${order.orderNumber ?? ''} on Z2Market - opens in a new tab`}
                  </span>
                </a>
              ) : (
                <div className={rowClass}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
